/**
 * What a contest still needs before it can be published — as structure, not
 * prose.
 *
 * The rules themselves are old: they have lived in `contest-detail-page.tsx` as
 * `buildPublishDisabledReason()`, a function whose only output was one sentence
 * on a disabled button's tooltip. That shape could tell an operator what was
 * missing but not *where to go*, and nothing else could reuse it. CAE-1545 asks
 * for the same conditions as a checklist card; CAE-1548 asks for them as the
 * Edit screens' Setup Rail. Both read this module, so the rail and the card
 * cannot drift apart — which is CAE-1548's third acceptance criterion.
 *
 * The sentence is still here, derived from the structure rather than built
 * alongside it, so the Publish button's wording is unchanged by the extraction.
 */
import { resolveFixtureLabel, resolvePickName } from '@s2w/core';
import type { ContestDetail } from '../api/contests';
import { parseContestAdapterFields } from '../utils/contest-adapter-config';
import { needsArtwork, reelStats } from './reel-stats';
import { formatList, sentenceCase } from '../utils/format-list';

/**
 * The reels every contest owes. Exported since CAE-1574: the Overview's Reels
 * section draws a slot per required reel so a never-created Reel 3 still appears
 * as one, and reading the same list is what keeps that section and this card's
 * "Create Reel 3" hint pointing at the same thing.
 */
/** The Reel count of a Contest whose payload carries none: every Contest had three before CAE-1566. */
export const DEFAULT_REEL_COUNT = 3;

/** Reels 1 to the Contest's own Reel count: the Reels publish holds it to (INV-11). */
export const requiredReelNumbers = (contest: { reelCount?: number }): number[] =>
  Array.from({ length: contest.reelCount ?? DEFAULT_REEL_COUNT }, (_, index) => index + 1);
/**
 * How many enabled options a reel owes before the contest can publish — a reel
 * is a choice, and one option is not a choice.
 *
 * Exported since CAE-1552: Edit › Reels draws a per-reel state dot and a warning
 * off the same threshold, and a second `2` written down over there is how that
 * rail ends up calling a reel fine while this module calls it short.
 */
export const MIN_ENABLED_OPTIONS_PER_REEL = 2;

/**
 * The tiers no Entry can reach (CAE-1607): a player picks once per reel, so the
 * most correct picks a contest can score is its reel count. A reel count lowered
 * after the ladder was built, or a deeper default seeded in, leaves tiers above
 * it that never pay. Publish refuses them (`PRIZE_TIER_UNREACHABLE`); Edit ›
 * Prizes marks the same rows off this function, so the two cannot disagree.
 */
export const unreachablePrizeTiers = <Tier extends { correctCount: number }>(
  contest: { reelCount?: number },
  prizeTiers: readonly Tier[],
): Tier[] => {
  const reelCount = contest.reelCount ?? DEFAULT_REEL_COUNT;
  return prizeTiers.filter((tier) => tier.correctCount > reelCount);
};

/**
 * The step of the four-route Edit flow that fixes a check. `$id` is the route
 * param, interpolated by the caller — these are TanStack route ids, not URLs.
 */
export type EditStepPath =
  | '/contests/$id/edit/basic'
  | '/contests/$id/edit/fixtures'
  | '/contests/$id/edit/reels'
  | '/contests/$id/edit/prizes';

export type ReadinessKey = 'campaign' | 'round' | 'fixtures' | 'reels' | 'prizes';

export interface ReadinessCheck {
  key: ReadinessKey;
  /** The checklist row's label — "Campaign ID set". */
  label: string;
  /** The value when done, what to do about it when not. */
  hint: string;
  done: boolean;
  /** Where the row's "Configure →" sends the operator. */
  href: EditStepPath;
  /**
   * The imperative phrases this check contributes to the Publish button's
   * sentence — "set a campaign ID", "create Reel 2 and Reel 3". Empty when done.
   *
   * A list rather than a string because `reels` is one row over three distinct
   * failures: a reel that does not exist, one with too few enabled options, and
   * one whose enabled options have no asset. The design rolls those into a
   * single checklist row, and rolling them into a single *phrase* too would
   * cost the operator the detail they need to act on.
   */
  requirements: readonly string[];
}

/**
 * One enabled Reel Option with no image, named for the checklist (CAE-1595).
 *
 * The reels check says *which reels* are short of artwork; this says which
 * options, which is what an Operator has to go and fix.
 */
export interface MissingImageOption {
  /** The Reel Option's id — the row's key, and nothing else. */
  id: string;
  /** The Reel it sits on, which is where its row links to. */
  reelNumber: number;
  /** What a Player would read on the spinner, override and all. */
  pickName: string;
  /**
   * The fixture the pick is about. `null` when no attached fixture matches —
   * see `missingImageOptions`.
   */
  fixtureLabel: string | null;
}

export interface PublishReadiness {
  checks: readonly ReadinessCheck[];
  doneCount: number;
  total: number;
  complete: boolean;
  /**
   * Every enabled Reel Option with no image, in Reel order then position order.
   *
   * Named under the reels row rather than in a second list of its own (decided
   * 6 Oct, James Gardner, at the grill): the row already says artwork is
   * outstanding, and the operator's next question is always *which picks*.
   */
  missingImageOptions: readonly MissingImageOption[];
}

const formatReelList = (reelNumbers: readonly number[]): string =>
  formatList(reelNumbers.map((reelNumber) => `Reel ${reelNumber}`));

/**
 * The reel conditions, kept apart from the check that presents them.
 *
 * Three failures, evaluated independently, because a contest can have all three
 * at once — reel 3 missing while reel 1 is short of options and reel 2's
 * options have no assets.
 */
const reelFailures = (detail: ContestDetail) => {
  const reelsByNumber = new Map(detail.reels.map((reel) => [reel.reelNumber, reel]));

  const required = requiredReelNumbers(detail.contest);
  const missing = required.filter((reelNumber) => !reelsByNumber.has(reelNumber));

  const needingOptions = required.filter((reelNumber) => {
    const reel = reelsByNumber.get(reelNumber);
    return reel
      ? reel.options.filter((option) => option.enabled).length < MIN_ENABLED_OPTIONS_PER_REEL
      : false;
  });

  /*
   * Through `reelStats` rather than its own `.some()`, since CAE-1552. Edit ›
   * Reels draws a state dot per reel and a warn banner off the same question,
   * and the two screens are one click apart — a rail calling a reel fine beside
   * a Setup Rail calling it amber is the drift this check exists to prevent.
   */
  const missingAssets = required.filter((reelNumber) => {
    const reel = reelsByNumber.get(reelNumber);
    return reel ? reelStats(reel.options, reel.kind).missingAssets > 0 : false;
  });

  /*
   * A Reel above the Reel count: built while the count was higher, and lowered since. Publish
   * refuses it (exactly Reels 1 to the count), and no endpoint deletes a Reel, so the way out is
   * the count back up to the highest Reel built.
   */
  const beyond = detail.reels
    .map((reel) => reel.reelNumber)
    .filter((reelNumber) => reelNumber > required.length)
    .sort((a, b) => a - b);

  return { missing, needingOptions, missingAssets, beyond };
};

/**
 * Every enabled Reel Option with no image — **exactly** the options publish
 * refuses with `REEL_OPTION_MISSING_ASSET` (INV-10, S2W-S27).
 *
 * Over `detail.reels` rather than over the required Reel numbers, which is
 * where this parts company with `reelFailures.missingAssets` above. Publish
 * walks every Reel the Contest has, so a Reel built while the Reel count was
 * higher and left behind when it was lowered still has its optionless-image
 * picks refused — and an operator told "Reel 2" while Reel 5 is the one holding
 * publish up has been told something true and useless. The row above is
 * unchanged: `beyond` already tells them to put the Reel count back, which is
 * the actual fix for that Reel.
 *
 * The *predicate* is `needsArtwork`, the one `reelStats.missingAssets` counts
 * with, so the checklist, the Setup Rail, the per-Reel banner and the rail dot
 * cannot disagree about whether an option needs artwork — only about how many
 * Reels they are looking at.
 */
const missingImageOptions = (detail: ContestDetail): readonly MissingImageOption[] => {
  const fixturesById = new Map(detail.fixtures.map((fixture) => [fixture.id, fixture]));

  return [...detail.reels]
    .sort((a, b) => a.reelNumber - b.reelNumber)
    .flatMap((reel) =>
      [...reel.options]
        .sort((a, b) => a.position - b.position)
        .filter((option) => needsArtwork(option, reel.kind))
        .map((option) => {
          const fixture = fixturesById.get(option.contestFixtureId);
          return {
            id: option.id,
            reelNumber: reel.reelNumber,
            pickName: resolvePickName(option),
            /*
             * `null` rather than a placeholder when the fixture is gone. A
             * detach removes its options with it, so this is the anomalous
             * case — a payload whose option outlived its fixture — and the row
             * still names the pick, which is the part the operator can act on.
             */
            fixtureLabel: fixture ? resolveFixtureLabel(fixture) : null,
          };
        }),
    );
};

export const getPublishReadiness = (detail: ContestDetail): PublishReadiness => {
  const { campaignId, round } = parseContestAdapterFields(detail.contest.adapterConfig);
  const fixtureCount = detail.fixtures.length;
  const prizeTierCount = detail.prizeTiers.length;
  const unreachableTierCount = unreachablePrizeTiers(detail.contest, detail.prizeTiers).length;
  const reelCount = requiredReelNumbers(detail.contest).length;
  const reels = reelFailures(detail);

  const reelRequirements = [
    ...(reels.beyond.length > 0
      ? [
          `set the reel count back to ${reels.beyond[reels.beyond.length - 1]} (${formatReelList(
            reels.beyond,
          )} ${reels.beyond.length === 1 ? 'is' : 'are'} built)`,
        ]
      : []),
    ...(reels.missing.length > 0 ? [`create ${formatReelList(reels.missing)}`] : []),
    ...(reels.needingOptions.length > 0
      ? [
          `add at least ${MIN_ENABLED_OPTIONS_PER_REEL} enabled options to ${formatReelList(
            reels.needingOptions,
          )}`,
        ]
      : []),
    ...(reels.missingAssets.length > 0
      ? [`attach assets to enabled options in ${formatReelList(reels.missingAssets)}`]
      : []),
  ];

  const prizeRequirements =
    prizeTierCount === 0
      ? ['configure at least one prize tier']
      : unreachableTierCount > 0
        ? [`lower or remove the prize tiers above ${reelCount} correct`]
        : [];

  const checks: readonly ReadinessCheck[] = [
    {
      key: 'campaign',
      label: 'Campaign ID set',
      hint: campaignId === '' ? 'Set the campaign ID' : campaignId,
      done: campaignId !== '',
      href: '/contests/$id/edit/basic',
      requirements: campaignId === '' ? ['set a campaign ID'] : [],
    },
    {
      key: 'round',
      label: 'Round set',
      hint: round === '' ? 'Select a round' : round,
      done: round !== '',
      href: '/contests/$id/edit/basic',
      requirements: round === '' ? ['set a round'] : [],
    },
    {
      key: 'fixtures',
      label: 'Fixture attached',
      /*
       * The fixture's *name* when there is one, which is what the design's
       * checklist asks for: the row already says a fixture is attached, so the
       * hint's job is to say which. Beyond one there is no single name to give
       * and no room on a 12.5px line to list them, so it falls back to a count.
       */
      hint:
        fixtureCount === 0
          ? 'Attach at least one fixture'
          : fixtureCount === 1
            ? resolveFixtureLabel(detail.fixtures[0]!)
            : `${fixtureCount} fixtures`,
      done: fixtureCount > 0,
      href: '/contests/$id/edit/fixtures',
      requirements: fixtureCount === 0 ? ['add at least one fixture'] : [],
    },
    {
      key: 'reels',
      label: 'Reels configured',
      // The rolled-up row keeps the specific failure: `formatList` of the same
      // phrases the sentence uses, so "Create Reel 3 and add at least 2 enabled
      // options to Reel 1" rather than a flat "Reels need configuring".
      hint:
        reelRequirements.length === 0
          ? `${requiredReelNumbers(detail.contest).length} reels`
          : sentenceCase(formatList(reelRequirements)),
      done: reelRequirements.length === 0,
      href: '/contests/$id/edit/reels',
      requirements: reelRequirements,
    },
    {
      key: 'prizes',
      label: 'Prize tiers configured',
      hint:
        prizeTierCount === 0
          ? 'Configure prize tiers'
          : unreachableTierCount > 0
            ? sentenceCase(prizeRequirements[0]!)
            : `${prizeTierCount} tier${prizeTierCount === 1 ? '' : 's'}`,
      done: prizeRequirements.length === 0,
      href: '/contests/$id/edit/prizes',
      requirements: prizeRequirements,
    },
  ];

  const doneCount = checks.filter((check) => check.done).length;

  return {
    checks,
    doneCount,
    total: checks.length,
    complete: doneCount === checks.length,
    missingImageOptions: missingImageOptions(detail),
  };
};

/**
 * The four steps of the Edit flow, which are also the Setup Rail's four
 * segments (design-system.md §6 "Setup rail").
 *
 * Not the same five things as `ReadinessCheck`, and deliberately so. The rail is
 * a *navigation* control: its segments are the screens an operator can be on, so
 * campaign and round — two checks that live on one screen — are one step here.
 */
export type SetupStepKey = 'basic' | 'fixtures' | 'reels' | 'prizes';

/**
 * The readiness checks that live on the Basic info screen, and so are the ones
 * its rail step has to cover beyond the form's own required fields.
 *
 * Named here rather than filtered out of `checks` by `href` because the step
 * reads these two *live*, from what the operator has typed, and a persisted
 * check cannot do that. `readiness.test.ts` pins this list against the checks
 * whose `href` is the Basic info screen, so the two cannot drift apart.
 */
export const BASIC_SCREEN_CHECKS: readonly ReadinessKey[] = ['campaign', 'round'];

export interface SetupStep {
  key: SetupStepKey;
  label: string;
  done: boolean;
  /**
   * Draws the step amber rather than idle.
   *
   * Not the complement of `done`, though it coincides with it on three of the
   * four steps. The ticket sets the rule per step: fixtures, reels and prizes
   * are `attn` whenever they are outstanding — an empty reel is something gone
   * wrong, not merely something not yet begun — while **fixtures** is also the
   * one that pulls the two apart in the other direction, going amber for a
   * fixture the feed has no markets for while staying `done`. Basic info is
   * never amber: an unfilled field is just unfilled.
   */
  attention: boolean;
  /** The count badge. Omitted on Basic info, which counts nothing. */
  count?: number;
}

export interface SetupStepSignals {
  /**
   * The *live* Basic info field values, not the persisted contest's.
   *
   * The rail re-renders as the operator types, which is what the prototype does
   * (`basic-info-app.jsx` derives the step from its own field state). Reading
   * `detail.contest` instead would leave the rail insisting Basic info is
   * outstanding while the operator looks at a form they have just filled in.
   */
  basic: { name: string; opensAt: string; campaignId: string; round: string };
  /**
   * How many attached fixtures the catalogue returned no markets for.
   *
   * Still passed in rather than derived, though CAE-1573 put `marketCount` on
   * `ContestDetail`'s fixtures and the Setup Rail now counts off that. The
   * signal stays a parameter because it is the *rail's* question — which
   * fixtures an operator has yet to sync — and `getPublishReadiness` answers a
   * different one for the Overview's card. Folding it in would make every
   * caller take a position on syncing to ask about readiness.
   */
  unsyncedFixtureCount?: number;
}

export const getSetupSteps = (
  detail: ContestDetail,
  signals: SetupStepSignals,
): readonly SetupStep[] => {
  const readiness = getPublishReadiness(detail);
  const isDone = (key: ReadinessKey) =>
    readiness.checks.find((check) => check.key === key)?.done ?? false;

  const { basic, unsyncedFixtureCount = 0 } = signals;
  const fixtureCount = detail.fixtures.length;
  const prizeTierCount = detail.prizeTiers.length;

  return [
    {
      key: 'basic',
      label: 'Basic information',
      /*
       * Every field on this screen that something is waiting on, read live.
       *
       * Name and opens-at are the form's own required fields — they gate Save,
       * not Publish. Campaign ID and round are the two readiness checks housed
       * here (`BASIC_SCREEN_CHECKS`, which `readiness.test.ts` holds to the set
       * of checks whose `href` points at this screen, so a sixth check landing
       * on it cannot quietly go unrepresented).
       *
       * The prototype hand-lists campaign ID and stops
       * (`requiredOk && campaignOk`). That is wrong the moment round is the only
       * thing missing: the rail reads 4/4, 100%, all green, beside a Publish
       * button disabled with "set a round" — the exact rail/card drift this
       * ticket's third acceptance criterion rules out. The prototype's mock
       * contest has a round set, so it never shows.
       *
       * Read from `signals` rather than the persisted contest so the rail moves
       * as the operator types, which is what the prototype does.
       */
      done:
        basic.name.trim() !== '' &&
        basic.opensAt.trim() !== '' &&
        basic.campaignId.trim() !== '' &&
        basic.round.trim() !== '',
      attention: false,
    },
    {
      key: 'fixtures',
      label: 'Fixtures',
      done: isDone('fixtures'),
      /*
       * Two different outstanding things, one amber.
       *
       * Nothing attached is the readiness check failing, and the step goes
       * amber for the same reason Reels and Prizes do — required setup the
       * contest cannot publish without (CAE-1550's empty state asks for this
       * by name). An attached fixture the feed has returned no markets for is
       * the other: the check passes, the step stays done, and the amber says
       * there is still work. The unsynced tally alone would miss the first
       * case entirely, since an empty contest has nothing to be unsynced.
       */
      attention: !isDone('fixtures') || unsyncedFixtureCount > 0,
      count: fixtureCount,
    },
    {
      key: 'reels',
      label: 'Reels',
      done: isDone('reels'),
      // Every way a reel can be wrong — missing, too few enabled options, an
      // enabled option with no asset — is already an unmet requirement, so the
      // rail needs no second rule here: not done is exactly "needs attention",
      // which is what the ticket asks for.
      attention: !isDone('reels'),
      count: detail.reels.length,
    },
    {
      key: 'prizes',
      label: 'Prizes',
      done: isDone('prizes'),
      // No tiers, or tiers above the reel count: both are the check failing.
      attention: !isDone('prizes'),
      count: prizeTierCount,
    },
  ];
};

/**
 * The order the Publish button's sentence names requirements in.
 *
 * Deliberately not `checks`' own order, which follows the design's checklist
 * (reels above prizes). This is the order the sentence has always used, and
 * operators read it on a button they look at every day — the extraction is not
 * the moment to reword it.
 */
const SENTENCE_ORDER: readonly ReadinessKey[] = [
  'campaign',
  'round',
  'fixtures',
  'prizes',
  'reels',
];

export const publishRequirementsSentence = (readiness: PublishReadiness): string | undefined => {
  const byKey = new Map(readiness.checks.map((check) => [check.key, check]));
  const requirements = SENTENCE_ORDER.flatMap((key) => byKey.get(key)?.requirements ?? []);

  if (requirements.length === 0) return undefined;
  return `${sentenceCase(formatList(requirements))} before publishing.`;
};
